import React, { useState, useRef, useEffect } from 'react';
import { Tabs, Card, Typography, Row, Col, Button, Progress, List, Divider, Grid, message, Space, Steps } from 'antd';
import { Pie, Bar } from '@ant-design/plots';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useOnboardingFlow } from '@/contexts/OnboardingFlowContext';
import { getAllTotals, hasWorkspaceStats } from '@/utils/workspaceStats';
import { updateWorkspaceOnboardingStep } from '@/api/workspaces';
import { normalizeOnboardingStep } from '@/utils/onboardingStep';
import { dashboardStats, getDashboardStatsByLanguage } from '@/mock/en/dashboard';
import './Onboarding.progress.css';

import { 
  // SettingOutlined, 
  HomeOutlined, 
  TeamOutlined, 
  UserOutlined, 
  DollarCircleOutlined, 
  ToolOutlined,
  // FileTextOutlined,
  ApartmentOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  RocketOutlined
} from '@ant-design/icons';

// Import new onboarding components
import { BlocksSelectionCard } from '@/components/onboarding/BlocksSelectionCard';
import { IndividualBlocksCard } from '@/components/onboarding/IndividualBlocksCard';
import { BulkBlocksCard } from '@/components/onboarding/BulkBlocksCard';
import { ResidentAssignmentCard } from '@/components/onboarding/ResidentAssignmentCard';
import { BulkResidentsCard } from '@/components/onboarding/BulkResidentsCard';

// Import modals
import WorkspaceEditModal from '@/components/features/workspace/WorkspaceEditModal';
import { BlockFormModal } from '@/components/features/resources/properties/BlockFormModal';
import ResidentFormModal from '@/components/features/resources/residents/ResidentFormModal';
import EmployeeFormModal from '@/components/features/resources/employees/EmployeeFormModal';
import DocumentSetupModal from '@/components/features/documents/DocumentSetupModal';
import MaintenanceSetupModal from '@/components/features/maintenance/MaintenanceSetupModal';

// Tour functionality removed

const { Title, Text, Paragraph } = Typography;

const STEP_LABEL: Record<
  'blocks' | 'individual-blocks' | 'bulk-blocks' |
  'residents' | 'bulk-residents' | 'invitation' | 'complete',
  string
> = {
  blocks: 'Block planning',
  'individual-blocks': 'Create blocks',
  'bulk-blocks': 'Bulk upload blocks',
  residents: 'Assign residents',
  'bulk-residents': 'Bulk upload residents',
  invitation: 'Invite residents',
  complete: 'Completed',
};

const STEP_PROMPT: Record<
  'blocks' | 'individual-blocks' | 'bulk-blocks' |
  'residents' | 'bulk-residents' | 'invitation' | 'complete',
  string
> = {
  blocks:
    'How many blocks do you have? If it is 5+ or 10+, bulk upload is usually faster.',
  'individual-blocks':
    'Manual block setup. Define floors/units clearly so resident assignment is smooth later.',
  'bulk-blocks':
    'Use CSV bulk upload for blocks. Once upload succeeds, move to resident onboarding.',
  residents:
    'Assign residents to units. You can proceed even if not all residents are added yet.',
  'bulk-residents':
    'Because the unit count is high, bulk resident upload is recommended. Prepare CSV and upload.',
  invitation:
    'Send invitations so residents can join. Start with a small batch, verify delivery, then send the rest.',
  complete:
    'Onboarding is complete. Next best step is opening help tours and inviting remaining residents.',
};

type OnboardingPageMode = 'onboarding' | 'dashboard';

const OnboardingPage: React.FC<{ mode?: OnboardingPageMode }> = ({ mode = 'onboarding' }) => {
  const { t, i18n } = useTranslation(['onboarding', 'dashboard', 'common']);
  // Tour functionality removed
  const { workspaceStats, firestoreUser, refreshWorkspaceStats, recalculateAndRefreshStats } = useAuth();
  const {
    setOnboardingCtx,
    flowData,
    stepSelectionRequest,
    setStepSelectionRequest,
  } = useOnboardingFlow();
  const [isRefreshingStats, setIsRefreshingStats] = useState(false);
  const screens = Grid.useBreakpoint();
  const sliderRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentOnboardingStep, setCurrentOnboardingStep] = useState<'blocks' | 'individual-blocks' | 'bulk-blocks' | 'residents' | 'bulk-residents' | 'invitation' | 'complete'>('blocks');
  
  
  const [selectedBlocksCount, setSelectedBlocksCount] = useState<number>(0);
  const [chatBlockLayoutDefaults, setChatBlockLayoutDefaults] = useState<{
    floors: number;
    unitsPerFloor: number;
  } | null>(null);
  const [openBlocksSummaryFromChat, setOpenBlocksSummaryFromChat] = useState(false);
  // const [allBlocksData, setAllBlocksData] = useState<any[]>([]);
  const [createdBlockIds, setCreatedBlockIds] = useState<string[]>([]);
  const [totalApartmentUnits, setTotalApartmentUnits] = useState<number>(0);
  // const {
  //   onboardingProgress,
  //   isOnboardingComplete,
  //   healthChecks,
  //   completedChecks,
  //   startTour,
  // Tour functionality removed

  // Modal states
  const [openWorkspaceModal, setOpenWorkspaceModal] = useState(false);
  const [openBlockModal, setOpenBlockModal] = useState(false);
  const [openResidentModal, setOpenResidentModal] = useState(false);
  const [openEmployeeModal, setOpenEmployeeModal] = useState(false);
  const [openDocumentModal, setOpenDocumentModal] = useState(false);
  const [openMaintenanceModal, setOpenMaintenanceModal] = useState(false);

  // Tour functionality removed - will be reimplemented from scratch

  // Get icon for each health check (unused - old health checks system)
  // const getHealthCheckIcon = (checkId: string) => {
  //   const iconMap: Record<string, React.ReactNode> = {
  //     'workspace-details': <SettingOutlined style={{ fontSize: '16px', color: '#1890ff' }} />,
  //     'properties': <HomeOutlined style={{ fontSize: '16px', color: '#52c41a' }} />,
  //     'residents': <TeamOutlined style={{ fontSize: '16px', color: '#722ed1' }} />,
  //     'employees': <UserOutlined style={{ fontSize: '16px', color: '#fa8c16' }} />,
  //     'documents': <FileTextOutlined style={{ fontSize: '16px', color: '#13c2c2' }} />,
  //     'maintenance': <ToolOutlined style={{ fontSize: '16px', color: '#eb2f96' }} />,
  //   };
  //   return iconMap[checkId] || <SettingOutlined style={{ fontSize: '16px', color: '#1890ff' }} />;
  // };

  // const handleActionClick = (checkId: string) => {
  //   switch (checkId) {
  //     case 'workspace-details':
  //       setOpenWorkspaceModal(true);
  //       break;
  //     case 'properties':
  //       setOpenBlockModal(true);
  //       break;
  //     case 'residents':
  //       setOpenResidentModal(true);
  //       break;
  //     case 'employees':
  //       setOpenEmployeeModal(true);
  //       break;
  //     case 'documents':
  //       setOpenDocumentModal(true);
  //       break;
  //     case 'maintenance':
  //       setOpenMaintenanceModal(true);
  //       break;
  //     default:
  //       break;
  //   }
  // };



  // Dashboard logic
  const currentLanguage = i18n.language;
  const languageSpecificStats = getDashboardStatsByLanguage(currentLanguage, t);

  // Handle stats refresh (recalculate and store)
  const handleRefreshStats = async () => {
    if (!firestoreUser) return;
    
    setIsRefreshingStats(true);
    try {
      await recalculateAndRefreshStats(); // This will recalculate and store the stats
      message.success('Workspace statistics recalculated and updated successfully!');
    } catch (error) {
      console.error('Error refreshing stats:', error);
      message.error('Failed to recalculate workspace statistics');
    } finally {
      setIsRefreshingStats(false);
    }
  };

  // Handle stats fetch (just fetch existing data)
  const handleFetchStats = async () => {
    if (!firestoreUser) return;
    
    setIsRefreshingStats(true);
    try {
      await refreshWorkspaceStats(); // This will just fetch existing stats
      message.success('Workspace statistics loaded successfully!');
    } catch (error) {
      console.error('Error fetching stats:', error);
      message.error('Failed to fetch workspace statistics');
    } finally {
      setIsRefreshingStats(false);
    }
  };

  const currentStats = React.useMemo(() => {
    // Always return 0 values when no real data is available
    const zeroStats = {
      totalProperties: 0,
      totalResidents: 0,
      totalEmployees: 0,
      activeMaintenance: 0,
      vacantUnits: 0,
      overduePayments: 0,
      rentCollection: 0,
    };

    if (hasWorkspaceStats(workspaceStats) && workspaceStats.blocks && Object.keys(workspaceStats.blocks).length > 0) {
      const totals = getAllTotals(workspaceStats);
      return {
        totalProperties: totals.totalBlocks || 0,
        totalResidents: totals.totalResidents || 0,
        totalEmployees: totals.totalEmployees || 0,
        activeMaintenance: totals.totalAssets || 0, // Use totalAssets as activeMaintenance for now
        vacantUnits: Math.max(0, (totals.totalApartmentUnits || 0) - (totals.totalResidents || 0)),
        overduePayments: 0, // This would need to be calculated from actual payment data
        rentCollection: 0, // This would need to be calculated from actual payment data
      };
    }
    
    return zeroStats;
  }, [workspaceStats]);

  const financeChartData = dashboardStats.financeData.flatMap(item => [
    { month: item.month, type: t('dashboard:revenue'), value: item.revenue },
    { month: item.month, type: t('dashboard:expenses'), value: item.expenses },
  ]);

  const financeConfig = {
    data: financeChartData,
    isGroup: true,
    xField: 'month',
    yField: 'value',
    seriesField: 'type',
    legend: { position: 'top' },
    color: ['#13c2c2', '#ff7875'],
    meta: {
      revenue: { alias: t('dashboard:revenue') },
      expenses: { alias: t('dashboard:expenses') },
    },
    height: screens.xs ? 220 : 300,
  };

  const maintenanceConfig = {
    data: languageSpecificStats.maintenanceData,
    angleField: 'value',
    colorField: 'type',
    label: {
      text: 'value',
      position: 'outside',
    },
    legend: {
      position: 'bottom',
    },
    color: ['#ff4d4f', '#1890ff', '#faad14', '#52c41a', '#bfbfbf'],
    height: screens.xs ? 220 : 300,
  };

  const stats = [
    {
      title: t('dashboard:totalProperties'),
      value: currentStats.totalProperties,
      icon: <HomeOutlined style={{ color: '#1890ff' }} />,
    },
    {
      title: t('dashboard:totalResidents'),
      value: currentStats.totalResidents,
      icon: <TeamOutlined style={{ color: '#52c41a' }} />,
    },
    {
      title: t('dashboard:totalEmployees'),
      value: currentStats.totalEmployees,
      icon: <UserOutlined style={{ color: '#faad14' }} />,
    },
    {
      title: t('dashboard:activeMaintenance'),
      value: currentStats.activeMaintenance,
      icon: <ToolOutlined style={{ color: '#eb2f96' }} />,
    },
    {
      title: t('dashboard:vacantUnits'),
      value: currentStats.vacantUnits,
      icon: <ApartmentOutlined style={{ color: '#b37feb' }} />,
    },
    {
      title: t('dashboard:overduePayments'),
      value: currentStats.overduePayments,
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
    },
    {
      title: t('dashboard:rentCollection'),
      value: currentStats.rentCollection,
      icon: <DollarCircleOutlined style={{ color: '#13c2c2' }} />,
    },
  ];

  const scrollToSlide = (index: number) => {
    if (sliderRef.current) {
      const cardWidth = 140;
      sliderRef.current.scrollTo({
        left: index * cardWidth,
        behavior: 'smooth'
      });
      setCurrentSlide(index);
    }
  };

  const handleScroll = () => {
    if (sliderRef.current) {
      const scrollLeft = sliderRef.current.scrollLeft;
      const cardWidth = 140;
      const newSlide = Math.round(scrollLeft / cardWidth);
      setCurrentSlide(newSlide);
    }
  };

  useEffect(() => {
    const slider = sliderRef.current;
    if (slider) {
      slider.addEventListener('scroll', handleScroll);
      return () => slider.removeEventListener('scroll', handleScroll);
    }
  }, []);


  // Track which tab is currently active
  const [activeTab, setActiveTab] = useState<OnboardingPageMode | 'welcome'>(
    mode === 'dashboard' ? 'dashboard' : 'onboarding'
  );
  // Track if we're on dashboard to prevent step detection
  const [isOnDashboard, setIsOnDashboard] = useState<boolean>(mode === 'dashboard');
  // Track if we've initialized the step from workspace data
  const isStepInitializedRef = useRef(false);
  
  // Progress from canonical onboarding step (0 blocks … 3 complete)
  const calculateProgress = (): number => {
    const raw = firestoreUser?.workspace?.onboardingStep ?? 0;
    const canonical = normalizeOnboardingStep(raw);
    return Math.round((canonical / 3) * 100);
  };

  useEffect(() => {
    setOnboardingCtx({
      step: currentOnboardingStep,
      progress: calculateProgress(),
      stepLabel: STEP_LABEL[currentOnboardingStep],
      stepPrompt: STEP_PROMPT[currentOnboardingStep],
      updatedAt: Date.now(),
    });
  }, [currentOnboardingStep, firestoreUser?.workspace?.onboardingStep, setOnboardingCtx]);

  useEffect(() => {
    if (currentOnboardingStep !== 'individual-blocks' && openBlocksSummaryFromChat) {
      setOpenBlocksSummaryFromChat(false);
    }
  }, [currentOnboardingStep, openBlocksSummaryFromChat]);
  
  // Get current step for Steps component (0-based)
  const getCurrentStep = (): number => {
    const raw = firestoreUser?.workspace?.onboardingStep ?? 0;
    return Math.min(normalizeOnboardingStep(raw), 3);
  };
  
  // Get step status for Steps component
  const getStepStatus = (stepIndex: number): 'wait' | 'process' | 'finish' | 'error' => {
    const currentStep = getCurrentStep();
    
    if (stepIndex < currentStep) return 'finish';
    if (stepIndex === currentStep) return 'process';
    return 'wait';
  };

  const applyExternalStepSelection = (
    rawStep: string | undefined,
    blockCount?: string,
    floors?: number,
    unitsPerFloor?: number,
    openSummary?: boolean
  ) => {
    if (!rawStep) return;
    isStepInitializedRef.current = true;

    if (rawStep === 'blocks' && blockCount) {
      if (blockCount === '5+' || blockCount === '10+') {
        setCurrentOnboardingStep('bulk-blocks');
        return;
      }
      if (blockCount === '1' || blockCount === '2' || blockCount === '3') {
        setSelectedBlocksCount(Number(blockCount));
        if (typeof floors === 'number' && typeof unitsPerFloor === 'number') {
          setChatBlockLayoutDefaults({ floors, unitsPerFloor });
        }
        setOpenBlocksSummaryFromChat(Boolean(openSummary));
        setCurrentOnboardingStep('individual-blocks');
        return;
      }
    }

    switch (rawStep) {
      case 'blocks':
      case 'invitation':
      case 'complete':
        setCurrentOnboardingStep(rawStep);
        return;
      case 'residents': {
        const tau = calculateTAUFromWorkspaceStats();
        setCurrentOnboardingStep(tau > 40 ? 'bulk-residents' : 'residents');
        return;
      }
      case 'bulk-blocks':
        setCurrentOnboardingStep('bulk-blocks');
        return;
      case 'individual-blocks':
        if (!blockCount && openSummary) {
          setSelectedBlocksCount((prev) => (prev > 0 ? prev : 1));
        }
        setOpenBlocksSummaryFromChat(Boolean(openSummary));
        if (typeof floors === 'number' && typeof unitsPerFloor === 'number') {
          setChatBlockLayoutDefaults({ floors, unitsPerFloor });
        }
        setCurrentOnboardingStep('individual-blocks');
        return;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!stepSelectionRequest?.step) return;
    applyExternalStepSelection(
      stepSelectionRequest.step,
      stepSelectionRequest.blockCount,
      stepSelectionRequest.floors,
      stepSelectionRequest.unitsPerFloor,
      stepSelectionRequest.openSummary
    );
    setStepSelectionRequest(null);
  }, [stepSelectionRequest, setStepSelectionRequest]);

  useEffect(() => {
    const flowBlockCount = flowData.blockCount;
    const flowFloors = flowData.defaultFloors;
    const flowUnits = flowData.defaultUnitsPerFloor;
    if (typeof flowBlockCount === 'number' && Number.isFinite(flowBlockCount)) {
      setSelectedBlocksCount((prev) => prev || Math.max(1, Math.floor(flowBlockCount)));
    }
    if (typeof flowFloors === 'number' &&
        Number.isFinite(flowFloors) &&
        typeof flowUnits === 'number' &&
        Number.isFinite(flowUnits)) {
      setChatBlockLayoutDefaults({
        floors: Math.floor(flowFloors),
        unitsPerFloor: Math.floor(flowUnits),
      });
    }
  }, [flowData]);

  // Handle step navigation (allow going back to previous steps and current step)
  const handleStepClick = (stepIndex: number) => {
    const currentStep = getCurrentStep();
    
    // Allow navigation to previous steps and current step (not future steps)
    if (stepIndex <= currentStep) {
      switch (stepIndex) {
        case 0: // Add Blocks
          setCurrentOnboardingStep('blocks');
          break;
        case 1: // Add Residents
          // Determine which resident step to show based on TAU
          const tau = calculateTAUFromWorkspaceStats();
          if (tau <= 40) {
            setCurrentOnboardingStep('residents');
          } else {
            setCurrentOnboardingStep('bulk-residents');
          }
          break;
        case 2: // Send Invitations
          setCurrentOnboardingStep('invitation');
          break;
        case 3: // Complete
          setCurrentOnboardingStep('complete');
          break;
        default:
          break;
      }
    }
  };

  // Utility function to calculate TAU from workspace stats
  const calculateTAUFromWorkspaceStats = (): number => {
    if (!workspaceStats?.blocks) return 0;
    return Object.values(workspaceStats.blocks).reduce((total, blockStats) => {
      return total + blockStats.totalApartmentUnits;
    }, 0);
  };

  // Track current workspace ID to detect workspace changes
  const currentWorkspaceIdRef = useRef<string | null>(null);
  
  // Initialize step from workspace data (only once per workspace)
  useEffect(() => {
    const workspaceId = firestoreUser?.workspace?.id;
    
    
    // Reset initialization flag if workspace changes
    if (workspaceId !== currentWorkspaceIdRef.current) {
      isStepInitializedRef.current = false;
      currentWorkspaceIdRef.current = workspaceId || null;
    }
    
    if (firestoreUser?.workspace && !isStepInitializedRef.current) {
      const raw = firestoreUser.workspace.onboardingStep ?? 0;
      const canonical = normalizeOnboardingStep(raw);
      let currentStep:
        | 'blocks'
        | 'individual-blocks'
        | 'bulk-blocks'
        | 'residents'
        | 'bulk-residents'
        | 'invitation'
        | 'complete' = 'blocks';

      switch (canonical) {
        case 0:
          currentStep = 'blocks';
          break;
        case 1: {
          const workspaceTau = calculateTAUFromWorkspaceStats();
          currentStep = workspaceTau > 40 ? 'bulk-residents' : 'residents';
          break;
        }
        case 2:
          currentStep = 'invitation';
          break;
        case 3:
        default:
          currentStep = 'complete';
          break;
      }

      setCurrentOnboardingStep(currentStep);
      isStepInitializedRef.current = true;
    }
  }, [firestoreUser?.workspace?.id]); // Only depend on workspace ID

  // Initialize data from workspace stats
  useEffect(() => {
    if (workspaceStats && workspaceStats.blocks) {
      const blockIds = Object.keys(workspaceStats.blocks);
      const totalUnits = calculateTAUFromWorkspaceStats();
      
      setCreatedBlockIds(blockIds);
      setTotalApartmentUnits(totalUnits);
    }
  }, [workspaceStats]);


  const renderOnboardingTab = () => {
    const handleBlocksNext = (selectedOption: string) => {
      
      // Parse the selected option to get the number of blocks
      let blocksCount = 0;
      if (selectedOption === '1') blocksCount = 1;
      else if (selectedOption === '2') blocksCount = 2;
      else if (selectedOption === '3') blocksCount = 3;
      else if (selectedOption === '5+') {
        // For 5+ blocks, show the bulk upload card
        setCurrentOnboardingStep('bulk-blocks');
        return;
      } else if (selectedOption === '10+') {
        // For 10+ blocks, show the bulk upload card
        setCurrentOnboardingStep('bulk-blocks');
        return;
      }
      
      if (blocksCount > 0) {
        setSelectedBlocksCount(blocksCount);
        setCurrentOnboardingStep('individual-blocks');
      }
    };

    const handleIndividualBlocksNext = async (blocksData: any[], _includeParking?: boolean, createdBlockIds?: string[]) => {
      // setAllBlocksData(blocksData);
      
      // Store created block IDs
      if (createdBlockIds) {
        setCreatedBlockIds(createdBlockIds);
      }
      
      // Calculate Total Apartment Units (TAU) from blocks data for local state
      const tau = blocksData.reduce((total, block) => {
        // const deletedFloors = block.deletedFloors?.length || 0;
        // const activeFloors = block.floors - deletedFloors;
        const deletedUnits = block.deletedUnits || {};
        const addedUnits = block.addedUnits || {};
        
        const blockUnits = Array.from({ length: block.floors }, (_, floorIndex) => {
          if (block.deletedFloors?.includes(floorIndex)) return 0;
          const deletedUnitsForFloor = deletedUnits[floorIndex] || [];
          const addedUnitsForFloor = addedUnits[floorIndex] || [];
          return block.unitsPerFloor - deletedUnitsForFloor.length + addedUnitsForFloor.length;
        }).reduce((sum, units) => sum + units, 0);
        
        return total + blockUnits;
      }, 0);
      
      setTotalApartmentUnits(tau);
      
      try {
        await updateWorkspaceOnboardingStep(firestoreUser!, 1);
        await refreshWorkspaceStats();
        
        // Use calculated TAU for immediate decision (workspace stats might not be updated yet)
        
        // Check TAU and decide next step based on calculated TAU (immediate decision)
        if (tau <= 40) {
          // Show resident assignment interface
          setCurrentOnboardingStep('residents');
        } else {
          // For TAU > 40, show bulk residents upload modal
          setCurrentOnboardingStep('bulk-residents');
        }
      } catch (error) {
        console.error('Error updating onboarding step:', error);
        message.error('Failed to update progress');
      }
    };

    const handleIndividualBlocksBack = () => {
      setCurrentOnboardingStep('blocks');
    };

    const handleBulkBlocksNext = async (createdBlockIds: string[]) => {
      // Store the created block IDs from bulk upload
      setCreatedBlockIds(createdBlockIds);
      
      try {
        await updateWorkspaceOnboardingStep(firestoreUser!, 1);
        await refreshWorkspaceStats();
        
        // For bulk upload, calculate TAU from workspace stats after creation
        // The BlockBulkUploadModal handles the creation and updates workspace stats
        const workspaceTau = calculateTAUFromWorkspaceStats();
        
        // Check TAU and decide next step based on workspace stats
        if (workspaceTau <= 40) {
          // Show resident assignment interface
          setCurrentOnboardingStep('residents');
        } else {
          // For TAU > 40, show bulk residents upload modal
          setCurrentOnboardingStep('bulk-residents');
        }
      } catch (error) {
        console.error('Error updating onboarding step:', error);
        message.error('Failed to update progress');
      }
    };

    const handleBulkBlocksBack = () => {
      setCurrentOnboardingStep('blocks');
    };

    const handleResidentAssignmentNext = async () => {
      try {
        await updateWorkspaceOnboardingStep(firestoreUser!, 2);
        await refreshWorkspaceStats();
        setCurrentOnboardingStep('invitation');
      } catch (error) {
        console.error('Error updating onboarding step:', error);
        message.error('Failed to update progress');
      }
    };

    const handleResidentAssignmentBack = () => {
      setCurrentOnboardingStep('individual-blocks');
    };

    const handleBulkResidentsNext = async () => {
      try {
        await updateWorkspaceOnboardingStep(firestoreUser!, 2);
        await refreshWorkspaceStats();
        setCurrentOnboardingStep('invitation');
      } catch (error) {
        console.error('Error updating onboarding step:', error);
        message.error('Failed to update progress');
      }
    };


    const handleInvitationNext = async () => {
      try {
        await updateWorkspaceOnboardingStep(firestoreUser!, 3);
        await refreshWorkspaceStats();
        setCurrentOnboardingStep('complete');
      } catch (error) {
        console.error('Error updating onboarding step:', error);
        message.error('Failed to update progress');
      }
    };

    const handleInvitationBack = () => {
      setCurrentOnboardingStep('residents');
    };




    
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px 16px'
      }}>
        {/* Progress Steps */}
        {activeTab === 'onboarding' && !isOnDashboard && (
          <div style={{
            width: '100%',
            maxWidth: 'min(1200px, 100%)',
            marginBottom: '24px',
            padding: '10px 20px 12px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef',
            textAlign: 'left',
          }}>
            <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '15px', lineHeight: 1.35, textAlign: 'left' }}>
              {t('onboarding:progress.title', 'Setup Progress')} - {calculateProgress()}%
            </Text>
            <Steps
              className="onboarding-progress-steps"
              size="small"
              labelPlacement="vertical"
              current={getCurrentStep()}
              onChange={handleStepClick}
              items={[
                {
                  title: t('onboarding:progress.steps.blocks', 'Add Blocks'),
                  description: t('onboarding:progress.steps.blocks.desc', 'Set up your apartment blocks, units and parking'),
                  status: getStepStatus(0)
                },
                {
                  title: t('onboarding:progress.steps.residents', 'Add Residents'),
                  description: t('onboarding:progress.steps.residents.desc', 'Assign residents to apartment units'),
                  status: getStepStatus(1)
                },
                {
                  title: t('onboarding:progress.steps.invitation', 'Send Invitations'),
                  description: t('onboarding:progress.steps.invitation.desc', 'Invite residents to the platform'),
                  status: getStepStatus(2)
                },
                {
                  title: t('onboarding:progress.steps.complete', 'Complete'),
                  description: t('onboarding:progress.steps.complete.desc', 'Finish onboarding'),
                  status: getStepStatus(3)
                }
              ]}
            />
          </div>
        )}

        {/* Blocks Selection Card */}
        {currentOnboardingStep === 'blocks' && activeTab === 'onboarding' && !isOnDashboard && (
          <div style={{
            width: '100%',
            maxWidth: '600px',
            opacity: 1,
            transition: 'all 0.5s ease-in-out'
          }}>
            <BlocksSelectionCard
              onNext={handleBlocksNext}
            />
          </div>
        )}

        {/* Individual Blocks Card */}
        {currentOnboardingStep === 'individual-blocks' && activeTab === 'onboarding' && !isOnDashboard && (
          <div style={{
            width: '100%',
            maxWidth: '600px',
            opacity: 1,
            transition: 'all 0.5s ease-in-out'
          }}>
            <IndividualBlocksCard
              numberOfBlocks={selectedBlocksCount}
              defaultLayout={chatBlockLayoutDefaults}
              openSummaryOnMount={openBlocksSummaryFromChat}
              onNext={handleIndividualBlocksNext}
              onBack={handleIndividualBlocksBack}
            />
          </div>
        )}

        {/* Bulk Blocks Card */}
        {currentOnboardingStep === 'bulk-blocks' && activeTab === 'onboarding' && !isOnDashboard && (
          <div style={{
            width: '100%',
            maxWidth: '600px',
            opacity: 1,
            transition: 'all 0.5s ease-in-out'
          }}>
            <BulkBlocksCard
              onNext={handleBulkBlocksNext}
              onBack={handleBulkBlocksBack}
            />
          </div>
        )}

        {/* Residents Card */}
        {currentOnboardingStep === 'residents' && activeTab === 'onboarding' && !isOnDashboard && (
          <div style={{
            width: '100%',
            maxWidth: '800px',
            opacity: 1,
            transition: 'all 0.5s ease-in-out'
          }}>
            <ResidentAssignmentCard
              blockIds={createdBlockIds}
              onNext={handleResidentAssignmentNext}
              onBack={handleResidentAssignmentBack}
            />
          </div>
        )}

        {/* Bulk Residents Card */}
        {currentOnboardingStep === 'bulk-residents' && activeTab === 'onboarding' && !isOnDashboard && (
          <div style={{
            width: '100%',
            maxWidth: '800px',
            opacity: 1,
            transition: 'all 0.5s ease-in-out'
          }}>
            <BulkResidentsCard
              onNext={handleBulkResidentsNext}
            />
          </div>
        )}

        {/* Invitation Card */}
        {currentOnboardingStep === 'invitation' && activeTab === 'onboarding' && !isOnDashboard && (
          <div style={{
            width: '100%',
            maxWidth: '600px',
            opacity: 1,
            transition: 'all 0.5s ease-in-out'
          }}>
            <Card
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <UserOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
                  <span>{t('onboarding:cards.invitation.title', 'Send Invitations')}</span>
                </div>
              }
              style={{ width: '100%' }}
            >
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ marginBottom: '24px' }}>
                  <UserOutlined style={{ fontSize: '64px', color: '#1890ff', marginBottom: '16px' }} />
                  <Typography.Title level={3} style={{ margin: '0 0 8px 0' }}>
                    {t('onboarding:cards.invitation.title', 'Send Invitations')}
                  </Typography.Title>
                  <Typography.Text type="secondary" style={{ fontSize: '16px' }}>
                    {t('onboarding:cards.invitation.description', 'Invite your residents to join the platform and start using the app.')}
                  </Typography.Text>
                </div>
                
                <div style={{ marginBottom: '32px' }}>
                  <Typography.Text>
                    {t('onboarding:cards.invitation.features', 'This step will allow you to:')}
                  </Typography.Text>
                  <ul style={{ textAlign: 'left', marginTop: '12px', paddingLeft: '20px' }}>
                    <li>{t('onboarding:cards.invitation.feature1', 'Send invitation emails to residents')}</li>
                    <li>{t('onboarding:cards.invitation.feature2', 'Set up resident accounts')}</li>
                    <li>{t('onboarding:cards.invitation.feature3', 'Configure notification preferences')}</li>
                    <li>{t('onboarding:cards.invitation.feature4', 'Complete the onboarding process')}</li>
                  </ul>
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <Button onClick={handleInvitationBack}>
                    {t('common:back', 'Back')}
                  </Button>
                  <Button type="primary" onClick={handleInvitationNext}>
                    {t('onboarding:cards.invitation.complete', 'Complete Setup')}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Complete Card */}
        {currentOnboardingStep === 'complete' && activeTab === 'onboarding' && !isOnDashboard && (
          <div style={{
            width: '100%',
            maxWidth: '600px',
            opacity: 1,
            transition: 'all 0.5s ease-in-out'
          }}>
            <Card style={{ width: '100%', maxWidth: 600, margin: '0 auto' }}>
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Title level={2}>Onboarding Complete!</Title>
                <Text type="secondary">
                  You have successfully set up your apartment complex with {totalApartmentUnits} apartment units.
                  {totalApartmentUnits > 40 && ' For bulk resident management, you can use the bulk upload feature from the residents page.'}
                </Text>
              </div>
            </Card>
          </div>
        )}
      </div>
    );
  };

  const renderWelcomeTab = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '24px 16px',
    }}>
      <Card style={{ maxWidth: 560, width: '100%', textAlign: 'center' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <RocketOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
            <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
              {t('help:tours.welcome.title')}
            </Title>
            <Paragraph style={{ fontSize: '16px', color: '#666', margin: '8px 0 0 0' }}>
              {t('onboarding:welcome.description', 'Set up your apartment management system step by step. Follow the progress below to complete your workspace configuration.')}
            </Paragraph>
          </div>

          <Button
            type="primary"
            size="large"
            icon={<RocketOutlined />}
            onClick={() => {
              window.location.href = '/help';
            }}
            style={{
              height: '48px',
              fontSize: '16px',
              background: 'linear-gradient(135deg, #1890ff 0%, #722ed1 100%)',
              border: 'none',
              boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)',
            }}
          >
            {t('help:tours.startTour')}
          </Button>
        </Space>
      </Card>
    </div>
  );

  const renderDashboardTab = () => {
    const isUsingRealStats = hasWorkspaceStats(workspaceStats) && workspaceStats.blocks && Object.keys(workspaceStats.blocks).length > 0;
    
    return (
      <div style={{ padding: '24px 0' }}>
        {/* Dashboard Header with Refresh Button */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '24px' 
        }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              {t('dashboard:title')}
            </Title>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {isUsingRealStats ? 'Real-time workspace statistics' : 'Using sample data - refresh to load real stats'}
            </Text>
          </div>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              loading={isRefreshingStats}
              onClick={handleFetchStats}
              size="small"
            >
              Load Stats
            </Button>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              loading={isRefreshingStats}
              onClick={handleRefreshStats}
              size="small"
            >
              Recalculate
            </Button>
          </Space>
        </div>

        {/* Enhanced Stats Section */}
        <div style={{ marginBottom: 24 }}>
        <div
          ref={sliderRef}
          style={{
            display: 'flex',
            overflowX: screens.xs ? 'auto' : 'visible',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            gap: screens.xs ? '12px' : '8px',
            padding: '8px 0',
            flexWrap: screens.xs ? 'nowrap' : 'wrap',
            justifyContent: screens.xs ? 'flex-start' : 'center'
          }}
        >
          {stats.map((stat, idx) => (
            <Card
              key={idx}
              style={{
                minWidth: 120,
                maxWidth: 150,
                width: '100%',
                flex: screens.xs ? '0 0 auto' : '1 1 0',
                cursor: 'pointer',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                border: '1px solid #f0f0f0',
                margin: screens.xs ? 0 : '0 4px'
              }}
              onClick={() => scrollToSlide(idx)}
              hoverable
              styles={{
                body: {
                  padding: '16px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }
              }}
            >
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                marginBottom: '4px'
              }}>
                <div style={{ fontSize: '20px' }}>
                  {stat.icon}
                </div>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: '700',
                  color: '#262626'
                }}>
                  {stat.value}
                </div>
              </div>
              <div style={{ 
                fontSize: '14px', 
                color: '#262626',
                textAlign: 'center',
                lineHeight: '1.2',
                fontWeight: '600'
              }}>
                {stat.title}
              </div>
            </Card>
          ))}
        </div>
        
        {/* Progress Indicators for Mobile */}
        {screens.xs && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: '6px', 
            marginTop: '12px' 
          }}>
            {stats.map((_, idx) => (
              <div
                key={idx}
                onClick={() => scrollToSlide(idx)}
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: currentSlide === idx ? '#1890ff' : '#d9d9d9',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease'
                }}
              />
            ))}
          </div>
        )}
      </div>
      <Divider />
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12} lg={8}>
          <Card title={t('dashboard:maintenanceManagement')} styles={{ body: { padding: screens.xs ? 8 : 24 } }}>
            <Pie {...maintenanceConfig} />
          </Card>
        </Col>
        <Col xs={24} md={12} lg={8}>
          <Card title={t('dashboard:financeChart')} styles={{ body: { padding: screens.xs ? 8 : 24 } }}>
            <Bar {...financeConfig} />
          </Card>
        </Col>
        <Col xs={24} md={24} lg={8}>
          <Card title={t('dashboard:rentCollection')} styles={{ body: { padding: screens.xs ? 8 : 24, textAlign: 'center' } }}>
            <Progress type="circle" percent={currentStats.rentCollection} strokeColor="#52c41a" size={screens.xs ? 120 : 180} />
            <div style={{ marginTop: 16, textAlign: 'center', fontSize: screens.xs ? 14 : 18 }}>{currentStats.rentCollection}% {t('dashboard:collected')}</div>
          </Card>
        </Col>
      </Row>
      <Divider />
      <Card style={{ marginTop: 8 }} title={t('dashboard:recentActivities')}>
        <List
          dataSource={languageSpecificStats.recentActivities}
          renderItem={item => (
            <List.Item>
              <List.Item.Meta
                title={item.title}
                description={item.time}
              />
            </List.Item>
          )}
          locale={{ emptyText: t('common:messages.noData') }}
        />
      </Card>
    </div>
  );
  };



  return (
    <div style={{ padding: '0' }}>
      {mode === 'onboarding' ? (
        renderOnboardingTab()
      ) : (
        <Tabs
          defaultActiveKey="dashboard"
          size="large"
          activeKey={activeTab}
          onChange={(activeKey) => {
            setActiveTab(activeKey as typeof activeTab);
            setIsOnDashboard(activeKey === 'dashboard');
          }}
          items={[
            {
              key: 'dashboard',
              label: t('dashboard.title'),
              children: renderDashboardTab(),
            },
            {
              key: 'welcome',
              label: t('help:tours.welcome.title', 'Welcome to your apartment'),
              children: renderWelcomeTab(),
            },
          ]}
        />
      )}

      {/* Modals */}
      <WorkspaceEditModal
        open={openWorkspaceModal}
        workspace={null}
        onCancel={() => setOpenWorkspaceModal(false)}
        onSuccess={() => {
          setOpenWorkspaceModal(false);
          // Refresh health checks
        }}
      />

      <BlockFormModal
        visible={openBlockModal}
        onCancel={() => setOpenBlockModal(false)}
        onSubmit={async (_values) => {
          // Handle block creation
          setOpenBlockModal(false);
          return 'block-id';
        }}
        title={t('dashboard.quickActions.addBlock')}
      />

      <ResidentFormModal
        visible={openResidentModal}
        onCancel={() => setOpenResidentModal(false)}
        onSubmit={(_values) => {
          // Handle resident creation
          setOpenResidentModal(false);
        }}
        title={t('dashboard.quickActions.addResident')}
      />

      <EmployeeFormModal
        visible={openEmployeeModal}
        onCancel={() => setOpenEmployeeModal(false)}
        onSubmit={(_values) => {
          setOpenEmployeeModal(false);
        }}
        title={t('dashboard.quickActions.addEmployee')}
      />

      <DocumentSetupModal
        visible={openDocumentModal}
        onCancel={() => setOpenDocumentModal(false)}
        onSuccess={() => {
          setOpenDocumentModal(false);
        }}
      />

      <MaintenanceSetupModal
        visible={openMaintenanceModal}
        onCancel={() => setOpenMaintenanceModal(false)}
        onSuccess={() => {
          setOpenMaintenanceModal(false);
        }}
      />
    </div>
  );
};

export default OnboardingPage;
